<script setup lang="ts">
import ChevronDownIcon from '@/assets/icons/ChevronDownIcon.vue';
import ChevronRightIcon from '@/assets/icons/ChevronRightIcon.vue';
import { useLastViewed } from '@/composables/useLastViewed';
import { computed, ref, toRefs, useId, watch } from 'vue';

const { setAccordionOpenById, getAccordionOpenById } = useLastViewed();

const emit = defineEmits<{
  'update:expanded': [value: boolean];
}>();

const props = withDefaults(
  defineProps<{
    accordionCollapseBodyStyle?: string;
    buttonStyle?: string;
    canClear?: boolean;
    defaultExpanded?: boolean;
    expanded?: boolean;
    identifier?: string;
    forceOpen?: boolean;
    showChevron?: boolean;
    leftPadding?: boolean;
  }>(),
  {
    accordionCollapseBodyStyle: '',
    buttonStyle: '',
    canClear: false,
    defaultExpanded: false,
    expanded: undefined,
    identifier: '',
    forceOpen: false,
    showChevron: true,
    leftPadding: true,
  },
);
const { identifier, canClear, leftPadding } = toRefs(props);

const id = ref<string>(useId());
const isBlinking = ref(false);
const isInvalid = ref(false);
const collapseIconStyle = 'size-icon-sm shrink-0 text-secondary';

const isAccordionOpen = computed<boolean>(
  () => props.forceOpen || (props.expanded ?? getAccordionOpenById(id.value, canClear.value, props.defaultExpanded)),
);

const setIsAccordionOpen = (value: boolean): void => {
  if (props.forceOpen) return;

  if (props.expanded !== undefined) {
    emit('update:expanded', value);
    return;
  }

  setAccordionOpenById(id.value, value, canClear.value);
};

const triggerBlink = (): void => {
  isBlinking.value = true;
  setTimeout(() => {
    isBlinking.value = false;
  }, 500);
};

const handleButtonClicked = (): void => {
  setIsAccordionOpen(!isAccordionOpen.value);
};

const show = (): void => {
  setIsAccordionOpen(true);
};

const hide = (): void => {
  setIsAccordionOpen(false);
};

const setIsInvalid = (value: boolean): void => {
  isInvalid.value = value;
};

watch(
  identifier,
  () => {
    id.value = identifier.value.toString() || useId();
  },
  { immediate: true },
);

watch(
  isAccordionOpen,
  (newVal) => {
    if (props.expanded === undefined) emit('update:expanded', newVal);
  },
  { immediate: true },
);

defineExpose({
  hide,
  show,
  triggerBlink,
  setIsInvalid,
  isOpen: isAccordionOpen,
});
</script>

<template>
  <div :id="`accordion-collapse-${id}`" data-accordion="collapse" class="w-full">
    <div :id="`accordion-collapse-heading-${id}`" class="flex items-center">
      <!-- Not a <button>: headers hold inputs and checkboxes, whose Space/Enter would also activate a surrounding button. -->
      <div
        role="button"
        tabindex="0"
        :class="[
          'outline-inner',
          'bg-surface-secondary rounded-md',
          'flex items-center w-full',
          'py-sm font-medium',
          leftPadding ? 'pl-md' : '',
          { 'animate-notification': isBlinking },
          { 'outline-inner-error': isInvalid },
          ...(buttonStyle ? [buttonStyle] : []),
          { '!rounded-b-none': isAccordionOpen },
        ]"
        :data-accordion-target="`#accordion-collapse-body-${id}`"
        :aria-expanded="isAccordionOpen"
        :aria-controls="`accordion-collapse-body-${id}`"
        @click="handleButtonClicked"
        @keydown.enter.self.prevent="handleButtonClicked"
        @keydown.space.self.prevent="handleButtonClicked"
      >
        <template v-if="showChevron">
          <ChevronDownIcon
            v-if="isAccordionOpen"
            :class="[collapseIconStyle, 'mr-smd', { 'opacity-20 cursor-not-allowed': props.forceOpen }]"
          />
          <ChevronRightIcon v-else :class="[collapseIconStyle, 'mr-smd']" />
        </template>
        <slot name="header" :expanded="isAccordionOpen"></slot>
      </div>
      <div v-if="$slots.actions" class="shrink-0">
        <slot name="actions" />
      </div>
    </div>
    <div
      v-if="isAccordionOpen"
      :id="`accordion-collapse-body-${id}`"
      :aria-labelledby="`accordion-collapse-heading-${id}`"
      :class="[
        'bg-surface-secondary rounded-b-md',
        'flex flex-col',
        ...(accordionCollapseBodyStyle ? [accordionCollapseBodyStyle] : []),
      ]"
    >
      <slot name="items"></slot>
      <slot name="footer"></slot>
    </div>
  </div>
</template>
